import { useState } from "react";
import { deleteUser } from "aws-amplify/auth";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { Button, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DialogShell } from "@/shared/components/ui";
import { ApiRequestError } from "@/shared/api/client";
import { deleteMyAccount } from "../profile.api";
import { useAuth } from "@/app/context/AuthContext";
import { useNotification } from "@/app/context/NotificationContext";

/** A deliberate two-step irreversible-data action, kept out of normal profile editing. */
export const AccountDeletionControl = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const close = () => {
    if (deleting) return;
    setOpen(false);
    setConfirmation("");
    setError(null);
  };

  const confirmDeletion = async () => {
    if (confirmation !== "DELETE") return;
    setDeleting(true);
    setError(null);
    try {
      await deleteMyAccount();
      await deleteUser();
      await logout();
      void navigate("/signup", { replace: true });
    } catch (cause) {
      const message =
        cause instanceof ApiRequestError
          ? cause.message
          : "Your Chessla data was removed, but we could not finish removing the sign-in. Please contact support.";
      setError(message);
      showNotification(message, "error");
      setDeleting(false);
    }
  };

  return (
    <>
      <Button color="secondary" variant="text" onClick={() => setOpen(true)} sx={{ px: 0, fontSize: "0.72rem" }}>
        Delete account
      </Button>
      <DialogShell
        open={open}
        onClose={close}
        title="DELETE YOUR ACCOUNT?"
        actions={
          <>
            <Button onClick={close} disabled={deleting}>
              Keep my account
            </Button>
            <Button
              color="secondary"
              variant="contained"
              disabled={confirmation !== "DELETE" || deleting}
              onClick={() => void confirmDeletion()}
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          This permanently removes your Chessla profile, games, analyses, and Nikola history. Cancel Nikola&apos;s Club
          first if it is active.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
          Type <strong>DELETE</strong> to confirm.
        </Typography>
        <TextField
          fullWidth
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          size="small"
          sx={{ mt: 1.5 }}
          inputProps={{ "aria-label": "Type DELETE to permanently remove your account" }}
        />
        {error && (
          <SnackbarNotice severity="error" sx={{ mt: 2, borderRadius: 0 }}>
            {error}
          </SnackbarNotice>
        )}
      </DialogShell>
    </>
  );
};
